import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { ScoreChart } from "@/components/ScoreChart";
import { FavoriteStar } from "@/components/FavoriteStar";
import { ChangePctCell } from "@/components/ChangePctCell";

export function IpDetailPage() {
  const { ip_id } = useParams<{ ip_id: string }>();
  const detail = useQuery({ queryKey: queryKeys.ip(ip_id!), queryFn: () => api.getIp(ip_id!) });
  const favorites = useQuery({ queryKey: queryKeys.favorites, queryFn: api.getFavorites });

  if (detail.isLoading) return <p className="text-sm text-muted-foreground">読み込み中…</p>;
  if (detail.error) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return <p className="text-sm">IP が見つかりません</p>;
    }
    return <p className="text-sm text-destructive">読み込みに失敗しました</p>;
  }
  const d = detail.data!;
  const isFav = (favorites.data?.favorite_ips || []).includes(d.ip.ip_id);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {d.ip.display_name}
            <FavoriteStar ipId={d.ip.ip_id} isFavorite={isFav} />
          </h1>
          <p className="text-sm text-muted-foreground">
            {d.ip.category || "-"} / {(d.ip.publisher as string) || "-"} / {(d.ip.release_year as number) || "-"}年
          </p>
        </div>
        <div className="text-right space-y-1">
          <div className="text-2xl font-semibold tabular-nums">{d.latest_total_score?.toFixed(1) ?? "-"}</div>
          <div className="text-xs flex gap-2">
            前週比 <ChangePctCell value={d.weekly_change_pct} />
            前月比 <ChangePctCell value={d.monthly_change_pct} />
          </div>
        </div>
      </header>

      <section className="space-y-2">
        <h2 className="text-sm font-medium">時系列スコア</h2>
        <ScoreChart data={d.time_series} />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-md border p-4 space-y-2">
          <h3 className="text-sm font-medium">Claude スコア(直近月次)</h3>
          <p className="text-3xl font-semibold tabular-nums">{d.claude_score ?? "-"}</p>
        </div>
        <div className="rounded-md border p-4 space-y-2">
          <h3 className="text-sm font-medium">類似 IP</h3>
          <ul className="text-sm space-y-1">
            {(d.similar_ips || []).map((id) => (
              <li key={id} className="text-muted-foreground">{id}</li>
            ))}
            {!d.similar_ips?.length && <li className="text-muted-foreground">-</li>}
          </ul>
        </div>
      </section>

      {d.fan_demographics && (
        <section className="rounded-md border p-4 space-y-2">
          <h3 className="text-sm font-medium">ファン層推定</h3>
          <pre className="text-xs whitespace-pre-wrap text-muted-foreground">
            {JSON.stringify(d.fan_demographics, null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}
